import CircleIcon from '@mui/icons-material/Circle'
import React from 'react'
import styled from 'styled-components'
import { Card } from 'sutra/components/Card/Card'
import { gray } from 'sutra/tokens/colors'
import { spacing } from 'sutra/tokens/spacing'

import type { FilterControlProps } from 'features/charts-v2'
import type { FilterCriterion } from 'features/trend-detection/types'
import { ENTITY_LABELS } from 'src/components/agent-forms/benchmark-entity-labels'
import type { BenchmarkingEntityType } from 'src/components/agent-forms/BenchmarkingAgentConfigForm'
import { BenchmarkFocusCard } from 'src/components/agent-forms/BenchmarkUniversePanel/BenchmarkFocusCard'
import { GroupSummaryRow } from 'src/components/agent-forms/BenchmarkUniversePanel/GroupSummaryRow'
import { UniverseFilterChiclets } from 'src/components/agent-forms/BenchmarkUniversePanel/UniverseFilterChiclets'
import type { BenchmarkingGroupStats } from 'src/components/agent-forms/use-benchmark-compare-stats'
import type { UseFocusEntitySelectionResult } from 'src/components/agent-forms/use-focus-entity-selection'

export interface BenchmarkUniversePanelProps {
  entityType: BenchmarkingEntityType | null

  universeFilterControlProps: FilterControlProps
  /** Raw universe (cohort) criteria — see `UniverseFilterChiclets`' `filters`
   * doc comment for why this is threaded alongside `universeFilterControlProps`
   * rather than derived from it. */
  universeFilters: FilterCriterion[]

  focus: UseFocusEntitySelectionResult

  universeStats: BenchmarkingGroupStats | null
  focusStats: BenchmarkingGroupStats | null
  statsLoading: boolean
  statsUnavailable: boolean
  /** Server-derived label for the trailing analysis window (e.g. "last 91
   * days") — see `BenchmarkingCompareStatsResponse.date_window_label`. */
  dateWindowLabel: string | null
}

/**
 * The Focus → Universe layout: the blue Focus card renders first (what
 * you're benchmarking), the "…to all other brands" / "…to all other
 * products" section follows (the boundary it's measured against). Together
 * with the caption chain above this panel ("What would you like to
 * benchmark?" → "Compare these brands…" → "…to all other brands", each
 * section title leading with an MUI icon — `Adjust` / `Circle`) the read
 * order tells the whole story without an explicit "nested inside" divider.
 * Replaces `BenchmarkComparisonPanel`'s side-by-side "Focus | VS | Benchmark"
 * cards, which read as two peers being compared rather than a subset
 * measured against a boundary.
 *
 * Pure view (no hooks), matching the component it replaces — every derived
 * value is a prop, so this renders identically given the same props whether
 * called from the create wizard, the customer edit panel, or the staff
 * settings modal.
 *
 * The Brands/Products entity picker (`BenchmarkEntityRadioGroup`) is NOT
 * rendered here — it decides the entity vocabulary for the whole panel, not
 * just the nested Focus card, so `BenchmarkingAgentConfigForm` renders it
 * above this card instead.
 *
 * There is now exactly ONE filter panel in this tree (the Universe side) —
 * `AgentFilterPanel` contributed two things to the old two-panel layout
 * (the header row + `[+]` portal and the popover host), and both are either suppressed or relocated into
 * `UniverseFilterChiclets` now, so mounting `AgentFilterPanel` here would
 * render nothing visible. This also means the duplicate-`id` hazard the old
 * `BenchmarkComparisonPanel` carried (two `AgentFilterPanel`s mounting a
 * popover-portal host under the same id at once) no longer applies — but the
 * failure mode it guarded against is still live: an agent-type swap (Issue ↔
 * Benchmarking rendering into the same `AgentCreateForm` slot) can still
 * mount one panel's tree before the outgoing one unmounts, so
 * `UniverseFilterChiclets` still mounts its popover host unconditionally and
 * hands the node directly to `filterPopoverPortalTarget` rather than
 * resolving it by id.
 */
export const BenchmarkUniversePanel: React.FC<BenchmarkUniversePanelProps> = ({
  entityType,
  universeFilterControlProps,
  universeFilters,
  focus,
  universeStats,
  focusStats,
  statsLoading,
  statsUnavailable,
  dateWindowLabel,
}) => {
  const universeTitle = entityType
    ? `…to all other ${ENTITY_LABELS[entityType].section.toLowerCase()}`
    : '…to all other brands or products'

  return (
    <UniverseCard>
      <FocusIndent>
        <BenchmarkFocusCard
          entityType={entityType}
          focus={focus}
          stats={focusStats}
          comparedTo={universeStats}
          statsLoading={statsLoading}
          statsUnavailable={statsUnavailable}
          dateWindowLabel={dateWindowLabel}
        />
      </FocusIndent>

      <ContentRow>
        <LeftColumn>
          <Title>
            <CircleIcon sx={{ fontSize: 14 }} />
            {universeTitle}
          </Title>
          <FilterCaption>
            With these filters applied to both groups:
          </FilterCaption>
          <UniverseFilterChiclets
            filters={universeFilters}
            filterControlProps={universeFilterControlProps}
          />
        </LeftColumn>
        <RightColumn>
          <GroupSummaryRow
            entityType={entityType}
            stats={universeStats}
            isLoading={statsLoading}
            isUnavailable={statsUnavailable}
            dateWindowLabel={dateWindowLabel}
          />
        </RightColumn>
      </ContentRow>
    </UniverseCard>
  )
}

const UniverseCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]}px;
  background: ${gray[100]};
`

const ContentRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[4]}px;
`

const LeftColumn = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]}px;
  margin-left: 8px;
`

const RightColumn = styled.div`
  flex: 0 0 auto;
  margin-right: 8px;
`

const Title = styled.h3`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'Poppins', system-ui, sans-serif;
  font-weight: 600;
  font-size: 16px;
  color: ${gray[900]};
  margin: 0;
`

const FilterCaption = styled.span`
  font-size: 11px;
  color: ${gray[500]};
`

const FocusIndent = styled.div`
  // padding-left: ${spacing[3]}px;
`
